import { htcmdService } from 'js/services/htcmd.service';

import {
    CONDITION_GROUPS,
    CONDITION_TYPE,
    URL_TAG_LAUNCH_CONDITION,
    NATIVE_TAG_LAUNCH_CONDITION
} from '../../../../constants';

/**
 * Split all conditions into two groups:
 *  - regular (when: on app init, on page render, on deposit, on another action etc)
 *  - additional (while: page is X, player is Z, balance is 0)
 */
export const groupConditions = (conditions) => {
    const regular = [];
    const additional = [];

    conditions.forEach((condition) => {
        const { conditionTypeId } = condition;

        if (CONDITION_GROUPS.regular.includes(conditionTypeId)) {
            regular.push(condition);
        }

        if (CONDITION_GROUPS.additional.includes(conditionTypeId)) {
            additional.push(condition);
        }
    });

    return {
        regular,
        additional
    };
};

export const conditionMethods = {
    [CONDITION_TYPE.basic]: ({ config }, data) => {
        const { launch } = config;

        return launch === data.launch;
    },
    [CONDITION_TYPE.page]: ({ config }, data) => {
        const { onPageLaunch } = config;
        const { page } = data;
        const pages = onPageLaunch
            .trim()
            .split(/(?:\s|,)\s*/)
            .filter(Boolean);

        return page && pages.includes(page);
    },
    [CONDITION_TYPE.url]: ({ config }, data) => {
        const { launchRule, urlParameters = [] } = config;
        const { urlParams } = data;

        if (!urlParameters.length) {
            return false;
        }

        const currentUrlParams = Object.fromEntries(
            Object.entries(urlParams)
                .map(([param, value]) => [param.trim().toLowerCase(), value.trim()]));
        const expectedUrlParams = urlParameters
            .map(({ name, value }) => ({ paramName: name.trim().toLowerCase(), expectedValue: value.trim() }));

        const allParamsPresent = expectedUrlParams
            .every(({ paramName, expectedValue }) => Object.keys(currentUrlParams).includes(paramName)
                && (!expectedValue || currentUrlParams[paramName] === expectedValue));
        const someParamsPresent = expectedUrlParams
            .some(({ paramName, expectedValue }) => Object.keys(currentUrlParams).includes(paramName)
                && (!expectedValue || currentUrlParams[paramName] === expectedValue));

        return launchRule === URL_TAG_LAUNCH_CONDITION.allParamsPresent ? allParamsPresent : !someParamsPresent;
    },
    [CONDITION_TYPE.native]: ({ config }) => {
        const { nativeAppRule } = config;
        const isNativeAppOnly = nativeAppRule === NATIVE_TAG_LAUNCH_CONDITION.nativeAppOnly;
        const isNativeApp = htcmdService.isEnabled;

        if (isNativeAppOnly) {
            return isNativeApp;
        }

        return !isNativeApp;
    }
};

export const runCondition = (condition, data) => {
    const fn = conditionMethods[condition.conditionTypeId];

    if (fn) {
        return fn(condition, data);
    }
};

export const canBeTriggered = (trigger, regular) => {
    const { data: { launch } } = trigger;

    return regular.some(item => item.config.launch === launch);
};

export const checkCondition = (trigger, regular, additional) => {
    const { type, data } = trigger;
    const condByType = regular.filter(item => item.conditionTypeId === type);

    return condByType.some((item) => {
        const fn = () => runCondition(item, data);
        const addFns = additional.map(item2 => () => runCondition(item2, data));

        return [fn, ...addFns].every(f => f());
    });
};
